import { useCallback, useEffect, useMemo, useState } from 'react';

// Hooks
import useSubscriptionClick from '../../../../../packages/hooks/useSubscriptionClick';
import useFetchIntervalData from '../../../../../packages/hooks/useFetchIntervalData';

// Utils
import dayjs from 'dayjs';
import { getValidatedDate } from '@sportal365/smp-widget-utils/src/date';
import { getISODate } from '../../utils/getISODate';
import { getMatches } from '../../../../../packages/services/football/matches';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { normalizeRoundMatches } from '../../normalizers/round/normalizeRoundMatchesResponseData';

// Types
import { IGeneralSubscriberInfo } from '../../../../../packages/constants/subscribeTypes';
import { IFootballConfig, INormalizedMatchV2, SDKOptions } from '../../../../../packages/types';

interface IUseRoundMatches extends IGeneralSubscriberInfo {
  tournament?: string;
  season?: string;
  stage?: string;
  round?: string;
  dataRefreshTime?: IFootballConfig['dataRefreshTime'];
  dataOddsDisplay?: IFootballConfig['dataOddsDisplay'];
  dataOddsMarketValueType?: IFootballConfig['dataOddsMarketValueType'];
  dataOddsBettingId?: IFootballConfig['dataOddsBettingId'];
  dataOddsMarket?: IFootballConfig['dataOddsMarket'];
  dataOddsPreEventOnly?: IFootballConfig['dataOddsPreEventOnly'];
  sdkOptions: SDKOptions;
  dataTeamNameType?: IFootballConfig['dataTeamNameType'];
  dataEntityLinks?: IFootballConfig['dataEntityLinks'];
  dataLimit?: string;
  dataSortDirection?: IFootballConfig['dataSortDirection'];
  dataDateFrom?: IFootballConfig['dataDateFrom'];
  dataDateTo?: IFootballConfig['dataDateTo'];
}

export default function useRoundMatches({
  subscribers,
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  dataOddsDisplay,
  dataOddsMarketValueType,
  dataOddsPreEventOnly,
  dataOddsBettingId,
  dataOddsMarket,
  dataTeamNameType,
  dataEntityLinks,
  dataLimit,
  sdkOptions,
  dataWidgetCuid,
  tournament,
  season,
  stage,
  round,
  dataSortDirection,
  dataDateFrom,
  dataDateTo,
}: IUseRoundMatches) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [matches, setMatches] = useState<INormalizedMatchV2[]>();

  const startDate = useMemo(() => {
    const validDate = getValidatedDate(dayjs, dataDateFrom?.date, dataDateFrom?.dateFormat);

    return validDate && getISODate(validDate, sdkOptions.dataConfigTimezone, true);
  }, [dataDateFrom, sdkOptions]);

  const endDate = useMemo(() => {
    const validDate = getValidatedDate(dayjs, dataDateTo?.date, dataDateTo?.dateFormat);

    return validDate && getISODate(validDate, sdkOptions.dataConfigTimezone, false);
  }, [dataDateTo, sdkOptions]);

  const round_filter = useMemo(() => {
    if (!stage || !round) return undefined;

    return [`${stage}:${round}`];
  }, [stage, round]);

  const params = {
    tournament_ids: tournament,
    season_ids: season,
    stage_ids: round_filter ? '' : stage,
    round_filter: round_filter,
    limit: dataLimit ?? '350',
    dataOddsDisplay: dataOddsDisplay,
    dataOddsBettingId: dataOddsBettingId,
    dataOddsMarket: dataOddsMarket,
    dataOddsMarketValueType: dataOddsMarketValueType,
    dataOddsPreEventOnly: dataOddsPreEventOnly,
    sort_direction: dataSortDirection,
    startDate: startDate,
    endDate: endDate,
  };

  const fetchRoundMatches = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      manageLoader && setIsLoading(true);
      if (stage && tournament) {
        getMatches({ params, sdkOptions })
          ?.then(({ matches }) => {
            manageLoader && setIsLoading(false);
            const normalizedMatches = normalizeRoundMatches({ matches, dataTeamNameType, dataEntityLinks, sdkOptions });
            setMatches(normalizedMatches);
            !isFromInterval && stackOdds(matches, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [
      dataOddsBettingId,
      dataOddsDisplay,
      dataOddsMarket,
      dataOddsMarketValueType,
      dataOddsPreEventOnly,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
      dataLimit,
      tournament,
      season,
      stage,
      round,
      dataSortDirection,
      startDate,
      endDate,
    ],
  );

  useFetchIntervalData(fetchRoundMatches, dataRefreshTime);

  useEffect(() => {
    fetchRoundMatches(true);
  }, [fetchRoundMatches]);

  return {
    showWidget: true,
    widgetData: { matches, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
}
